import * as React from "react";
import { useTranslation } from "react-i18next";

// --- Icons ---
import { ChevronDownIcon } from "@/components/_organisms/tiptap/tiptap-icons/chevron-down-icon";
import { LineHeightIcon } from "@/components/_organisms/tiptap/tiptap-icons/line-height-icon";
// --- Tiptap UI ---
import {
  canSetLineHeight,
  isLineHeightActive,
  LineHeightButton,
  lineHeightOptions,
  type LineHeightType,
} from "@/components/_organisms/tiptap/tiptap-ui/line-height-button/line-height-button";
// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_organisms/tiptap/tiptap-ui-primitive/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { type Editor } from "@tiptap/react";

export interface LineHeightDropdownProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The line heights to display in the dropdown.
   */
  lineHeights?: LineHeightType[];
  /**
   * Whether the dropdown should be hidden when no line heights are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function canSetAnyLineHeight(editor: Editor | null, lineHeights: LineHeightType[]): boolean {
  if (!editor) return false;
  return lineHeights.some((lineHeight) => canSetLineHeight(editor, lineHeight.value));
}

export function isAnyLineHeightActive(editor: Editor | null, lineHeights: LineHeightType[]): boolean {
  if (!editor) return false;
  return lineHeights.some((lineHeight) => isLineHeightActive(editor, lineHeight.value));
}

export function getFilteredLineHeightOptions(availableLineHeights: LineHeightType[]): typeof lineHeightOptions {
  return lineHeightOptions.filter((option) =>
    availableLineHeights.some((available) => available.value === option.value),
  );
}

export function shouldShowLineHeightDropdown(params: {
  editor: Editor | null;
  lineHeights: LineHeightType[];
  hideWhenUnavailable: boolean;
  canSetAny: boolean;
}): boolean {
  const { hideWhenUnavailable, canSetAny } = params;

  if (hideWhenUnavailable && !canSetAny) {
    return false;
  }

  return true;
}

export function useLineHeightDropdownState(editor: Editor | null, availableLineHeights: LineHeightType[]) {
  const [isOpen, setIsOpen] = React.useState(false);

  const filteredLineHeights = React.useMemo(
    () => getFilteredLineHeightOptions(availableLineHeights),
    [availableLineHeights],
  );

  const canSetAny = canSetAnyLineHeight(editor, availableLineHeights);
  const isAnyActive = isAnyLineHeightActive(editor, availableLineHeights);

  const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
    setIsOpen(open);
    callback?.(open);
  }, []);

  return {
    isOpen,
    setIsOpen,
    filteredLineHeights,
    canSetAny,
    isAnyActive,
    handleOpenChange,
  };
}

export function LineHeightDropdown({
  editor: providedEditor,
  lineHeights: lineHeightsProp,
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: LineHeightDropdownProps) {
  const { t } = useTranslation("common");
  const lineHeights = React.useMemo(
    () =>
      lineHeightsProp ?? [
        { label: t("editor.lineHeightSingle"), value: "1" },
        { label: "1.15", value: "1.15" },
        { label: "1.5", value: "1.5" },
        { label: t("editor.lineHeightDouble"), value: "2" },
        { label: "2.5", value: "2.5" },
        { label: "3", value: "3" },
      ],
    [lineHeightsProp, t],
  );
  const editor = useTiptapEditor(providedEditor);

  const { isOpen, filteredLineHeights, canSetAny, isAnyActive, handleOpenChange } = useLineHeightDropdownState(
    editor,
    lineHeights,
  );

  const show = React.useMemo(() => {
    return shouldShowLineHeightDropdown({
      editor,
      lineHeights,
      hideWhenUnavailable,
      canSetAny,
    });
  }, [editor, lineHeights, hideWhenUnavailable, canSetAny]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={isAnyActive ? "on" : "off"}
          aria-label={t("editor.lineHeightOptions")}
          tooltip={t("editor.lineHeight")}
          {...props}
        >
          <LineHeightIcon className="tiptap-button-icon" />
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {filteredLineHeights.map((option) => (
            <DropdownMenuItem key={option.value} asChild>
              <LineHeightButton
                editor={editor}
                lineHeight={option.value}
                text={option.label}
                hideWhenUnavailable={hideWhenUnavailable}
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LineHeightDropdown;
